/**
 * Notification Service
 *
 * Business logic for creating and routing notifications based on ticket activities.
 * Implements ownership-based routing:
 * - User actions → Assigned tech (or team if unassigned)
 * - IT actions → Ticket requester (user)
 *
 * Features:
 * - Activity to notification type mapping
 * - Smart message generation
 * - Deduplication via activityId
 * - Team notifications for unassigned tickets
 */

import type { Ticket, AssignmentGroup } from '../types/ticket';
import type { TicketActivity } from '../types/ticketActivity';
import { ActivityType, AuthorType, ActivityVisibility } from '../types/ticketActivity';
import type { CreateNotificationDTO, Notification } from '../types/notification';
import {
  NotificationType,
  RecipientType,
  shouldNotifyUserOnStatusChange,
} from '../types/notification';
import type { INotificationRepository } from '../data/repositories/interfaces/INotificationRepository';

/**
 * Notification routing result
 */
interface NotificationRouting {
  shouldNotify: boolean;
  recipientId: string | null;
  recipientType: RecipientType | null;
  notificationType: NotificationType | null;
}

/**
 * Notification Service class
 * Handles all notification creation and routing logic
 */
export class NotificationService {
  constructor(private notificationRepo: INotificationRepository) {}

  /**
   * Process an activity and create appropriate notifications.
   * Main entry point for the notification system.
   */
  async processActivity(
    activity: TicketActivity,
    ticket: Ticket
  ): Promise<Notification | null> {
    // Check if this activity should trigger notifications
    if (!this.shouldTriggerNotification(activity)) {
      return null;
    }

    // Determine routing
    const routing = this.determineRouting(activity, ticket);

    if (!routing.shouldNotify || !routing.recipientId || !routing.notificationType) {
      return null;
    }

    // Check for duplicates
    const exists = await this.notificationRepo.exists(activity.id, routing.recipientId);
    if (exists) {
      return null;
    }

    // Generate notification content
    const dto = this.buildNotificationDTO(
      activity,
      ticket,
      routing.recipientId,
      routing.recipientType!,
      routing.notificationType
    );

    // Create and return notification
    return this.notificationRepo.create(dto);
  }

  /**
   * Create a queue alert notification for an unassigned ticket.
   * Called when a new ticket enters a queue without assignment.
   */
  async createQueueAlert(
    ticket: Ticket,
    assignmentGroup: AssignmentGroup
  ): Promise<Notification | null> {
    // Check for duplicate queue alert
    const queueAlertId = `queue-${ticket.id}`;
    const exists = await this.notificationRepo.exists(queueAlertId, assignmentGroup);
    if (exists) {
      return null;
    }

    const dto: CreateNotificationDTO = {
      recipientId: assignmentGroup,
      recipientType: RecipientType.TEAM,
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      activityId: queueAlertId,
      type: NotificationType.QUEUE_ALERT,
      title: `New ticket in ${assignmentGroup} queue`,
      message: `${ticket.shortDescription}`,
      actorId: ticket.requesterId,
      actorName: ticket.requesterName,
      assignmentGroup,
    };

    return this.notificationRepo.create(dto);
  }

  /**
   * Create an escalation notification for the target team.
   */
  async createEscalationNotification(
    activity: TicketActivity,
    ticket: Ticket,
    newGroup: AssignmentGroup
  ): Promise<Notification | null> {
    // Check for duplicate
    const exists = await this.notificationRepo.exists(activity.id, newGroup);
    if (exists) {
      return null;
    }

    const dto: CreateNotificationDTO = {
      recipientId: newGroup,
      recipientType: RecipientType.TEAM,
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      activityId: activity.id,
      type: NotificationType.ESCALATION,
      title: `Ticket escalated to ${newGroup}`,
      message: activity.escalationReason || `${ticket.shortDescription}`,
      actorId: activity.authorId,
      actorName: activity.authorName,
      assignmentGroup: newGroup,
    };

    return this.notificationRepo.create(dto);
  }

  /**
   * Create an assignment change notification for the new assignee.
   */
  async createAssignmentNotification(
    activity: TicketActivity,
    ticket: Ticket,
    newTechId: string,
    newTechName: string
  ): Promise<Notification | null> {
    // Check for duplicate
    const exists = await this.notificationRepo.exists(activity.id, newTechId);
    if (exists) {
      return null;
    }

    const dto: CreateNotificationDTO = {
      recipientId: newTechId,
      recipientType: RecipientType.TECH,
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      activityId: activity.id,
      type: NotificationType.ASSIGNMENT_CHANGE,
      title: `Ticket assigned to you`,
      message: `${ticket.shortDescription}`,
      actorId: activity.authorId,
      actorName: activity.authorName,
    };

    return this.notificationRepo.create(dto);
  }

  /**
   * Check if an activity should trigger notifications.
   */
  private shouldTriggerNotification(activity: TicketActivity): boolean {
    // Explicit flag takes precedence
    if (activity.triggersNotification === false) {
      return false;
    }

    // Work notes are internal - never notify users
    if (activity.type === ActivityType.WORK_NOTE) {
      return false;
    }

    // Internal visibility activities don't notify users
    if (activity.visibility === ActivityVisibility.INTERNAL) {
      return false;
    }

    // System activities generally don't trigger notifications unless flagged
    if (activity.authorType === AuthorType.SYSTEM && !activity.triggersNotification) {
      return false;
    }

    // Edits don't trigger new notifications
    if (activity.type === ActivityType.EDITED) {
      return false;
    }

    // These activity types trigger notifications
    const notifyingTypes: ActivityType[] = [
      ActivityType.COMMENT,
      ActivityType.STATUS_CHANGE,
      ActivityType.RESOLVED,
      ActivityType.REOPENED,
      ActivityType.QUESTION_TO_USER,
      ActivityType.ANSWER_FROM_USER,
      ActivityType.ESCALATED,
      ActivityType.ASSIGNED,
    ];

    return notifyingTypes.includes(activity.type);
  }

  /**
   * Determine who should receive a notification for an activity.
   */
  private determineRouting(activity: TicketActivity, ticket: Ticket): NotificationRouting {
    const noNotify: NotificationRouting = {
      shouldNotify: false,
      recipientId: null,
      recipientType: null,
      notificationType: null,
    };

    // IT Staff actions → notify the ticket requester (user)
    if (activity.authorType === AuthorType.IT_STAFF) {
      return this.routeITAction(activity, ticket);
    }

    // User actions → notify the assigned tech (or team if unassigned)
    if (activity.authorType === AuthorType.USER) {
      return this.routeUserAction(activity, ticket);
    }

    return noNotify;
  }

  /**
   * Route IT staff actions to the ticket requester.
   */
  private routeITAction(activity: TicketActivity, ticket: Ticket): NotificationRouting {
    const recipientId = ticket.requesterId;
    const recipientType = RecipientType.USER;

    // Map activity to notification type for IT → User notifications
    const notificationType = this.mapITActivityToNotificationType(activity);

    if (!notificationType) {
      return {
        shouldNotify: false,
        recipientId: null,
        recipientType: null,
        notificationType: null,
      };
    }

    return {
      shouldNotify: true,
      recipientId,
      recipientType,
      notificationType,
    };
  }

  /**
   * Route user actions to the assigned tech or team.
   */
  private routeUserAction(activity: TicketActivity, ticket: Ticket): NotificationRouting {
    // Map activity to notification type for User → IT notifications
    const notificationType = this.mapUserActivityToNotificationType(activity);

    if (!notificationType) {
      return {
        shouldNotify: false,
        recipientId: null,
        recipientType: null,
        notificationType: null,
      };
    }

    // Route to assigned tech if available
    if (ticket.assignedTechId) {
      return {
        shouldNotify: true,
        recipientId: ticket.assignedTechId,
        recipientType: RecipientType.TECH,
        notificationType,
      };
    }

    // Route to assignment group if no tech assigned
    if (ticket.assignmentGroup) {
      return {
        shouldNotify: true,
        recipientId: ticket.assignmentGroup,
        recipientType: RecipientType.TEAM,
        notificationType,
      };
    }

    // No valid recipient
    return {
      shouldNotify: false,
      recipientId: null,
      recipientType: null,
      notificationType: null,
    };
  }

  /**
   * Map IT staff activity to notification type for user.
   */
  private mapITActivityToNotificationType(activity: TicketActivity): NotificationType | null {
    switch (activity.type) {
      case ActivityType.COMMENT:
        return NotificationType.TICKET_UPDATE;

      case ActivityType.QUESTION_TO_USER:
        return NotificationType.QUESTION_RECEIVED;

      case ActivityType.STATUS_CHANGE:
        // Only notify for certain status changes
        if (activity.newStatus && shouldNotifyUserOnStatusChange(activity.newStatus)) {
          return NotificationType.STATUS_CHANGE;
        }
        return null;

      case ActivityType.RESOLVED:
        return NotificationType.TICKET_RESOLVED;

      default:
        return null;
    }
  }

  /**
   * Map user activity to notification type for IT staff.
   */
  private mapUserActivityToNotificationType(activity: TicketActivity): NotificationType | null {
    switch (activity.type) {
      case ActivityType.COMMENT:
        return NotificationType.NEW_REPLY;

      case ActivityType.ANSWER_FROM_USER:
        return NotificationType.QUESTION_ANSWERED;

      case ActivityType.REOPENED:
        return NotificationType.TICKET_REOPENED;

      default:
        return null;
    }
  }

  /**
   * Build the notification DTO with generated content.
   */
  private buildNotificationDTO(
    activity: TicketActivity,
    ticket: Ticket,
    recipientId: string,
    recipientType: RecipientType,
    notificationType: NotificationType
  ): CreateNotificationDTO {
    const { title, message } = this.generateNotificationContent(
      activity,
      ticket,
      notificationType,
      recipientType
    );

    return {
      recipientId,
      recipientType,
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      activityId: activity.id,
      type: notificationType,
      title,
      message,
      actorId: activity.authorId,
      actorName: activity.authorName,
      newStatus: activity.newStatus,
      assignmentGroup: activity.newGroup,
    };
  }

  /**
   * Generate notification title and message based on type and context.
   */
  private generateNotificationContent(
    activity: TicketActivity,
    ticket: Ticket,
    notificationType: NotificationType,
    recipientType: RecipientType
  ): { title: string; message: string } {
    const ticketRef = this.formatTicketNumber(ticket.ticketNumber);

    switch (notificationType) {
      case NotificationType.TICKET_UPDATE:
        return {
          title: `Update on ${ticketRef}`,
          message: this.truncateMessage(activity.content || 'New comment added'),
        };

      case NotificationType.QUESTION_RECEIVED:
        return {
          title: `Question on ${ticketRef}`,
          message: this.truncateMessage(activity.questionText || 'IT has a question for you'),
        };

      case NotificationType.STATUS_CHANGE:
        return {
          title: `Status changed on ${ticketRef}`,
          message: `Status updated to ${activity.newStatus}`,
        };

      case NotificationType.TICKET_RESOLVED:
        return {
          title: `${ticketRef} resolved`,
          message: 'Your ticket has been resolved. Please review and confirm.',
        };

      case NotificationType.NEW_REPLY:
        return {
          title: `Reply on ${ticketRef}`,
          message: this.truncateMessage(
            activity.content || `${activity.authorName} replied`
          ),
        };

      case NotificationType.QUESTION_ANSWERED:
        return {
          title: `Answer received on ${ticketRef}`,
          message: this.truncateMessage(
            activity.content || `${activity.authorName} answered your question`
          ),
        };

      case NotificationType.TICKET_REOPENED:
        return {
          title: `${ticketRef} reopened`,
          message: `${activity.authorName} reopened this ticket`,
        };

      case NotificationType.ESCALATION:
        return {
          title: `${ticketRef} escalated`,
          message: activity.escalationReason || 'Ticket has been escalated',
        };

      case NotificationType.QUEUE_ALERT:
        return {
          title: `New ticket in queue`,
          message: ticket.shortDescription,
        };

      case NotificationType.ASSIGNMENT_CHANGE:
        return {
          title: `${ticketRef} assigned to you`,
          message: ticket.shortDescription,
        };

      default:
        return {
          title: `Update on ${ticketRef}`,
          message: 'Ticket activity',
        };
    }
  }

  /**
   * Format ticket number for display (strip common prefixes).
   */
  private formatTicketNumber(ticketNumber: string): string {
    // Keep it concise for notifications
    const prefixPattern = /^(TKT|INC|REQ|CHG|PRB|SR)/i;
    return ticketNumber.replace(prefixPattern, '').trim() || ticketNumber;
  }

  /**
   * Truncate message to reasonable length for notification preview.
   */
  private truncateMessage(message: string, maxLength: number = 100): string {
    if (message.length <= maxLength) {
      return message;
    }
    return message.substring(0, maxLength - 3) + '...';
  }
}

/**
 * Create a NotificationService instance.
 * Factory function for dependency injection.
 */
export function createNotificationService(
  notificationRepo: INotificationRepository
): NotificationService {
  return new NotificationService(notificationRepo);
}
